import { supabase } from '@/integrations/supabase/client';

export interface UsageCheckResult {
  allowed: boolean;
  currentUsage: number;
  limit: number | null;
  remaining: number | null;
  error?: string;
}

/**
 * Check if user can perform an analysis and increment usage if allowed
 */
export async function checkAndIncrementUsage(): Promise<UsageCheckResult> {
  try {
    const { data, error } = await supabase.rpc('increment_analysis_usage');
    
    if (error) {
      console.error('Usage check error:', error);
      return {
        allowed: false,
        currentUsage: 0,
        limit: null,
        remaining: null,
        error: error.message
      };
    }
    
    if (!data.success) {
      return {
        allowed: false,
        currentUsage: data.current_usage || 0,
        limit: data.limit,
        remaining: 0,
        error: data.error
      };
    }
    
    return {
      allowed: true,
      currentUsage: data.new_usage,
      limit: data.limit,
      remaining: data.limit ? data.limit - data.new_usage : null
    };
  } catch (err) {
    console.error('Usage service error:', err);
    return {
      allowed: false,
      currentUsage: 0,
      limit: null,
      remaining: null,
      error: err instanceof Error ? err.message : 'Unknown error'
    };
  }
}

/**
 * Get current usage without incrementing
 */
export async function getCurrentUsage(): Promise<{ used: number; limit: number | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { used: 0, limit: null };
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        analyses_used_this_month,
        subscription_plans!subscription_plan_id (
          analyses_limit
        )
      `)
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !data) {
      return { used: 0, limit: null };
    }
    
    return {
      used: data.analyses_used_this_month || 0,
      limit: (data.subscription_plans as any)?.analyses_limit || null
    };
  } catch {
    return { used: 0, limit: null };
  }
}
